/**
 * usePomodoroNotifications - Manages Pomodoro session notifications.
 *
 * Subscribes to 'pomodoro:session-complete' events via eventBus.
 * On session complete:
 * 1) Plays a short audio notification (Web Audio API beep)
 * 2) Shows a Desktop Notification if permission is granted
 * 3) Triggers a visual banner/toast via callback
 *
 * Also handles:
 * - Requesting Notification permission on first work session start
 * - Providing ARIA live region content for timer state changes
 *
 * @see Requirements 7.3, 14.7, 14.8
 */

import { useEffect, useCallback, useRef, useState } from 'react';
import { eventBus } from '@/services/eventBus';
import { usePomodoroStore } from '@/stores/pomodoroStore';
import type { PomodoroPhase } from '@/stores/pomodoroStore';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface PomodoroNotificationState {
  /** Whether a visual banner is currently showing. */
  showBanner: boolean;
  /** The banner message to display. */
  bannerMessage: string;
  /** The suggested next action ("Start Break" or "Start Work Session"). */
  bannerAction: string;
  /** The current ARIA live region announcement text. */
  ariaAnnouncement: string;
  /** Whether the screen flash effect is active. */
  isFlashing: boolean;
  /** Dismiss the notification banner. */
  dismissBanner: () => void;
  /** Start the next phase (called from the banner action button). */
  startNextPhase: () => void;
}

// ─── Audio Notification ──────────────────────────────────────────────────────

/**
 * Plays a short, pleasant beep using the Web Audio API.
 * Uses two tones (rising) for a pleasant "ding" sound.
 */
function playNotificationSound(): void {
  try {
    const audioContext = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();

    // First tone
    const oscillator1 = audioContext.createOscillator();
    const gainNode1 = audioContext.createGain();
    oscillator1.connect(gainNode1);
    gainNode1.connect(audioContext.destination);
    oscillator1.frequency.setValueAtTime(587.33, audioContext.currentTime); // D5
    oscillator1.type = 'sine';
    gainNode1.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode1.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
    oscillator1.start(audioContext.currentTime);
    oscillator1.stop(audioContext.currentTime + 0.3);

    // Second tone (slightly higher, delayed)
    const oscillator2 = audioContext.createOscillator();
    const gainNode2 = audioContext.createGain();
    oscillator2.connect(gainNode2);
    gainNode2.connect(audioContext.destination);
    oscillator2.frequency.setValueAtTime(880, audioContext.currentTime + 0.15); // A5
    oscillator2.type = 'sine';
    gainNode2.gain.setValueAtTime(0, audioContext.currentTime);
    gainNode2.gain.setValueAtTime(0.3, audioContext.currentTime + 0.15);
    gainNode2.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
    oscillator2.start(audioContext.currentTime + 0.15);
    oscillator2.stop(audioContext.currentTime + 0.5);

    // Cleanup audio context after sounds finish
    setTimeout(() => {
      void audioContext.close();
    }, 1000);
  } catch {
    // Audio playback failed silently — visual notification still shows
  }
}

// ─── Desktop Notification ────────────────────────────────────────────────────

/**
 * Request Notification API permission if not already decided.
 */
export async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (!('Notification' in window)) {
    return 'denied';
  }
  if (Notification.permission === 'granted' || Notification.permission === 'denied') {
    return Notification.permission;
  }
  return Notification.requestPermission();
}

/**
 * Show a desktop notification with the given title and body.
 */
function showDesktopNotification(title: string, body: string): void {
  if (!('Notification' in window) || Notification.permission !== 'granted') {
    return;
  }
  try {
    new Notification(title, {
      body,
      icon: '/favicon.ico',
      tag: 'pomodoro-session-complete',
    });
  } catch {
    // Desktop notification failed silently
  }
}

// ─── Helper: Format time for ARIA announcements ──────────────────────────────

function formatTimeForAria(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (mins === 0) {
    return `${secs} second${secs !== 1 ? 's' : ''}`;
  }
  if (secs === 0) {
    return `${mins} minute${mins !== 1 ? 's' : ''}`;
  }
  return `${mins} minute${mins !== 1 ? 's' : ''} and ${secs} second${secs !== 1 ? 's' : ''}`;
}

function getPhaseDisplayName(phase: PomodoroPhase): string {
  switch (phase) {
    case 'work':
      return 'work session';
    case 'shortBreak':
      return 'short break';
    case 'longBreak':
      return 'long break';
    case 'idle':
      return 'idle';
  }
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export function usePomodoroNotifications(): PomodoroNotificationState {
  const [showBanner, setShowBanner] = useState(false);
  const [bannerMessage, setBannerMessage] = useState('');
  const [bannerAction, setBannerAction] = useState('');
  const [ariaAnnouncement, setAriaAnnouncement] = useState('');
  const [isFlashing, setIsFlashing] = useState(false);

  // Track the last phase to detect transitions
  const lastPhaseRef = useRef<PomodoroPhase>('idle');
  const lastAnnouncementTimeRef = useRef<number>(0);
  const hasRequestedPermissionRef = useRef(false);

  const phase = usePomodoroStore((s) => s.phase);
  const remainingSeconds = usePomodoroStore((s) => s.remainingSeconds);
  const isRunning = usePomodoroStore((s) => s.isRunning);
  const isPaused = usePomodoroStore((s) => s.isPaused);
  const startBreak = usePomodoroStore((s) => s.startBreak);
  const startWork = usePomodoroStore((s) => s.startWork);

  // ─── Request Notification permission on first work session start ─────────
  useEffect(() => {
    if (phase === 'work' && !hasRequestedPermissionRef.current) {
      hasRequestedPermissionRef.current = true;
      void requestNotificationPermission();
    }
  }, [phase]);

  // ─── Subscribe to session-complete events ────────────────────────────────
  useEffect(() => {
    const unsubscribe = eventBus.subscribe('pomodoro:session-complete', (payload) => {
      const { sessionCount: currentSessionCount, config: currentConfig } =
        usePomodoroStore.getState();

      // Determine what just completed based on what the store transitioned from.
      // After completeSession(), the store is in 'idle'. We figure out what completed
      // by looking at the payload and the session count.
      const justCompletedWork = payload.duration > 0;

      // Determine notification message
      let message: string;
      let action: string;
      let desktopTitle: string;
      let desktopBody: string;

      // After work session completes, suggest break
      if (currentSessionCount > 0 || justCompletedWork) {
        const isLongBreakNext = currentSessionCount >= currentConfig.sessionsBeforeLongBreak;
        if (isLongBreakNext) {
          message = 'Great work! Time for a long break.';
          action = 'Start Long Break';
          desktopTitle = 'Focus session complete!';
          desktopBody = `You've completed ${currentConfig.sessionsBeforeLongBreak} sessions. Take a well-deserved long break!`;
        } else {
          message = 'Focus session complete! Take a short break.';
          action = 'Start Break';
          desktopTitle = 'Focus session complete!';
          desktopBody = `Session ${currentSessionCount}/${currentConfig.sessionsBeforeLongBreak} done. Time for a short break!`;
        }
      } else {
        // Break completed, suggest work
        message = "Break's over! Ready for the next focus session?";
        action = 'Start Work Session';
        desktopTitle = "Break's over!";
        desktopBody = 'Time to get back to focused work.';
      }

      // 1) Play audio notification
      playNotificationSound();

      // 2) Show desktop notification
      showDesktopNotification(desktopTitle, desktopBody);

      // 3) Show visual banner
      setBannerMessage(message);
      setBannerAction(action);
      setShowBanner(true);

      // 4) Visual flash (accessibility alternative to audio)
      setIsFlashing(true);
      setTimeout(() => setIsFlashing(false), 600);

      // 5) Update ARIA announcement
      setAriaAnnouncement(`${desktopTitle} ${desktopBody}`);
    });

    return unsubscribe;
  }, []);

  // ─── ARIA live region updates for timer state ────────────────────────────
  // Announce at appropriate intervals:
  // - Every minute for timers above 5 minutes
  // - Every 15 seconds for timers below 5 minutes
  useEffect(() => {
    if (!isRunning || phase === 'idle') {
      return;
    }

    const now = Date.now();
    const interval = remainingSeconds > 300 ? 60 : 15; // 5 minutes = 300 seconds
    const timeSinceLastAnnouncement = (now - lastAnnouncementTimeRef.current) / 1000;

    // Only announce at the specified interval
    if (
      timeSinceLastAnnouncement >= interval &&
      remainingSeconds > 0 &&
      remainingSeconds % interval === 0
    ) {
      const phaseName = getPhaseDisplayName(phase);
      const timeStr = formatTimeForAria(remainingSeconds);
      setAriaAnnouncement(`${timeStr} remaining in ${phaseName}`);
      lastAnnouncementTimeRef.current = now;
    }
  }, [remainingSeconds, isRunning, phase]);

  // ─── Announce phase transitions ──────────────────────────────────────────
  useEffect(() => {
    if (phase !== lastPhaseRef.current) {
      const prevPhase = lastPhaseRef.current;
      lastPhaseRef.current = phase;

      if (phase === 'work') {
        setAriaAnnouncement('Work session started. Focus time begins now.');
        lastAnnouncementTimeRef.current = Date.now();
      } else if (phase === 'shortBreak') {
        setAriaAnnouncement('Short break started. Take a moment to rest.');
        lastAnnouncementTimeRef.current = Date.now();
      } else if (phase === 'longBreak') {
        setAriaAnnouncement('Long break started. You have earned a longer rest.');
        lastAnnouncementTimeRef.current = Date.now();
      } else if (phase === 'idle' && prevPhase !== 'idle') {
        // Session completed — announcement handled by event listener
      }
    }
  }, [phase]);

  // ─── Announce pause/resume ───────────────────────────────────────────────
  useEffect(() => {
    if (phase === 'idle') return;
    if (isPaused) {
      setAriaAnnouncement('Timer paused.');
    }
  }, [isPaused, phase]);

  // ─── Callbacks ───────────────────────────────────────────────────────────

  const dismissBanner = useCallback(() => {
    setShowBanner(false);
    setBannerMessage('');
    setBannerAction('');
  }, []);

  const startNextPhase = useCallback(() => {
    const { sessionCount: currentSessionCount } =
      usePomodoroStore.getState();

    // If we just finished work sessions, start a break
    if (currentSessionCount > 0) {
      startBreak();
    } else {
      // If we just finished a break, start work
      startWork();
    }

    setShowBanner(false);
    setBannerMessage('');
    setBannerAction('');
  }, [startBreak, startWork]);

  return {
    showBanner,
    bannerMessage,
    bannerAction,
    ariaAnnouncement,
    isFlashing,
    dismissBanner,
    startNextPhase,
  };
}
